/**
 * OptionsDropdown Component
 * Manages the options dropdown menu (Chart, Delete, etc.)
 * Handles dropdown visibility and action delegation
 */

import {
  querySelectorAll,
  querySelector,
  toggleClass,
  removeClass,
  addEventListener,
} from '../ui/DOMHelpers';
import { SELECTORS, CLASS_NAMES } from '../utils/constants';

/**
 * Options dropdown action types
 */
export type DropdownAction = 'chart' | 'delete' | 'custom';

/**
 * Callback for dropdown actions
 */
export type ActionCallback = (action: DropdownAction, data?: unknown) => void;

/**
 * OptionsDropdown manages dropdown menus and their actions
 */
export class OptionsDropdown {
  private buttons: HTMLElement[];
  private dropdowns: HTMLElement[];
  private onAction?: ActionCallback;
  private documentClickHandler?: () => void;

  /**
   * Create a new OptionsDropdown instance
   * @param onAction - Callback to invoke when an action is selected
   */
  constructor(onAction?: ActionCallback) {
    this.buttons = [];
    this.dropdowns = [];
    this.onAction = onAction;
  }

  /**
   * Initialize the options dropdown
   * Finds all dropdown elements and sets up event listeners
   */
  initialize(): void {
    // Find all options buttons and dropdowns
    this.buttons = querySelectorAll<HTMLElement>(SELECTORS.OPTIONS_BUTTON);
    this.dropdowns = querySelectorAll<HTMLElement>(SELECTORS.OPTIONS_DROPDOWN);

    if (this.buttons.length === 0 || this.dropdowns.length === 0) {
      console.warn('OptionsDropdown: No dropdown elements found');
      return;
    }

    if (this.buttons.length !== this.dropdowns.length) {
      console.warn(
        `OptionsDropdown: Mismatch between buttons (${this.buttons.length}) and dropdowns (${this.dropdowns.length})`
      );
    }

    this.setupEventListeners();
  }

  /**
   * Set up event listeners for dropdowns
   */
  private setupEventListeners(): void {
    // Set up button click handlers (paired with dropdowns by index)
    this.buttons.forEach((button, index) => {
      const dropdown = this.dropdowns[index];
      if (!dropdown) return;

      addEventListener(button, 'click', (e) => {
        e.stopPropagation();
        this.toggleDropdown(dropdown);
      });

      // Set up option item handlers within this dropdown
      this.setupDropdownItems(dropdown);
    });

    // Close all dropdowns when clicking outside
    this.documentClickHandler = () => {
      this.closeAll();
    };
    document.addEventListener('click', this.documentClickHandler);
  }

  /**
   * Set up event listeners for items within a dropdown
   * @param dropdown - The dropdown element
   */
  private setupDropdownItems(dropdown: HTMLElement): void {
    const items = querySelectorAll<HTMLElement>(SELECTORS.OPTION_ITEM, dropdown);

    items.forEach((item) => {
      addEventListener(item, 'click', (e) => {
        e.stopPropagation();
        this.handleItemClick(item, dropdown);
      });
    });
  }

  /**
   * Handle click on a dropdown item
   * @param item - The item that was clicked
   * @param dropdown - The dropdown containing the item
   */
  private handleItemClick(item: HTMLElement, dropdown: HTMLElement): void {
    const actionText = item.textContent?.trim() || '';

    // Determine the action type
    let action: DropdownAction;
    let data: unknown;

    if (actionText === 'Chart Selected Companies') {
      action = 'chart';
      data = this.getSelectedCompanies();
    } else if (actionText === 'Delete') {
      action = 'delete';
      data = this.getSelectedCompanies();
    } else {
      action = 'custom';
      data = { actionText };
    }

    // Close the dropdown
    this.close(dropdown);

    // Invoke callback if provided
    if (this.onAction) {
      this.onAction(action, data);
    }
  }

  /**
   * Get selected company names from checkboxes
   * @returns Array of selected company names
   */
  private getSelectedCompanies(): string[] {
    const checkboxes = querySelectorAll<HTMLInputElement>(
      '.comps-table tbody input[type="checkbox"]:checked'
    );

    return checkboxes
      .map((checkbox) => {
        const row = checkbox.closest('tr');
        if (!row) return null;

        // Company name is typically in the second column (index 1)
        const nameCell = querySelector<HTMLTableCellElement>('td:nth-child(2)', row);
        return nameCell?.textContent?.trim() || null;
      })
      .filter((name): name is string => name !== null);
  }

  /**
   * Toggle a specific dropdown
   * Closes all other dropdowns first
   * @param dropdown - The dropdown to toggle
   */
  private toggleDropdown(dropdown: HTMLElement): void {
    // Close all other dropdowns first
    this.dropdowns.forEach((d) => {
      if (d !== dropdown) {
        removeClass(d, CLASS_NAMES.SHOW);
      }
    });

    // Toggle this dropdown
    toggleClass(dropdown, CLASS_NAMES.SHOW);
  }

  /**
   * Close a specific dropdown
   * @param dropdown - The dropdown to close
   */
  close(dropdown: HTMLElement): void {
    removeClass(dropdown, CLASS_NAMES.SHOW);
  }

  /**
   * Close all dropdowns
   */
  closeAll(): void {
    this.dropdowns.forEach((dropdown) => {
      removeClass(dropdown, CLASS_NAMES.SHOW);
    });
  }

  /**
   * Set the action callback
   * @param callback - Callback to invoke when an action is selected
   */
  setOnAction(callback: ActionCallback): void {
    this.onAction = callback;
  }

  /**
   * Check if any dropdown is currently open
   * @returns True if any dropdown is open, false otherwise
   */
  isAnyDropdownOpen(): boolean {
    return this.dropdowns.some((dropdown) =>
      dropdown.classList.contains(CLASS_NAMES.SHOW)
    );
  }

  /**
   * Cleanup event listeners
   * Call this when destroying the component
   */
  destroy(): void {
    // Remove document click handler
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
      this.documentClickHandler = undefined;
    }

    // Clear references
    this.buttons = [];
    this.dropdowns = [];
    this.onAction = undefined;
  }
}
